import type { StaffMember } from './Staff';
import { TILE, type TileType } from './Tile';
import type { Vehicle } from './Vehicle';

/**
 * ParkingLot model (spec section 7).
 *
 * Pure serializable data. The grid `tiles` is the single source of truth for
 * layout and capacity; derived values (capacity, occupancy) are computed by
 * ParkingLotEngine rather than stored, so they can never drift from the grid.
 *
 * Finance / quality / pricing / staff fields are declared per spec but are
 * defaults in Phase 2; later phases populate them.
 */
export interface ParkingLot {
  id: string;
  name: string;
  districtId: string;
  level: number;

  // --- grid (Phase 2) ---
  width: number;
  height: number;
  /** Row-major flat grid, length = width * height; index = y * width + x. */
  tiles: TileType[];
  /** Number of currently occupied parking tiles (Phase 3+). */
  occupiedSpaces: number;

  // --- live vehicle simulation (Phase 3) ---
  /** Vehicles currently in/around the lot. */
  vehicles: Vehicle[];
  /** Game-minutes since the last spawn attempt (fixed-cadence spawner). */
  spawnTimerMinutes: number;
  /** Monotonic counter for generating unique vehicle ids within this lot. */
  vehicleSeq: number;
  /** Monotonic counter for exit payment-queue ordering. */
  paymentSeq: number;
  /** Lifetime count of vehicles that parked and left normally. */
  vehiclesServed: number;
  /** Lifetime count of vehicles that left without parking (no space/patience). */
  vehiclesRejected: number;
  /** Rolling average search/wait time before parking (game-minutes). */
  avgWaitMinutes: number;
  /** Rolling rejection rate (0..1), EMA over resolved arrivals. */
  rejectionRateEma: number;

  // --- finance (Phase 4) ---
  cash: number;
  revenue: number;
  expenses: number;
  profit: number;
  rent: number;
  electricityCost: number;
  maintenanceCost: number;
  staffCost: number;
  systemCost: number;

  // --- quality (Phase 6/7) ---
  reputation: number;
  cleanliness: number;
  security: number;
  trafficEfficiency: number;
  customerSatisfaction: number;

  // --- pricing (Phase 5) ---
  parkingPrice: number;
  pricingPolicy: string;

  // --- staff (Phase 6) + collections (later phases) ---
  staff: StaffMember[];
  /** Monotonic counter for generating unique staff ids within this lot. */
  staffSeq: number;
  upgrades: string[];
  facilities: string[];
}

export interface CreateLotOptions {
  id: string;
  name: string;
  districtId: string;
  width?: number;
  height?: number;
}

/** Default parking-lot grid size (spec section 8). */
export const DEFAULT_LOT_WIDTH = 20;
export const DEFAULT_LOT_HEIGHT = 20;

export function createParkingLot(options: CreateLotOptions): ParkingLot {
  const width = options.width ?? DEFAULT_LOT_WIDTH;
  const height = options.height ?? DEFAULT_LOT_HEIGHT;
  const tiles: TileType[] = new Array<TileType>(width * height).fill(TILE.Empty);

  return {
    id: options.id,
    name: options.name,
    districtId: options.districtId,
    level: 1,

    width,
    height,
    tiles,
    occupiedSpaces: 0,

    vehicles: [],
    spawnTimerMinutes: 0,
    vehicleSeq: 0,
    paymentSeq: 0,
    vehiclesServed: 0,
    vehiclesRejected: 0,
    avgWaitMinutes: 0,
    rejectionRateEma: 0,

    cash: 0,
    revenue: 0,
    expenses: 0,
    profit: 0,
    rent: 0,
    electricityCost: 0,
    maintenanceCost: 0,
    staffCost: 0,
    systemCost: 0,

    reputation: 50,
    cleanliness: 100,
    security: 50,
    trafficEfficiency: 100,
    customerSatisfaction: 100,

    parkingPrice: 40,
    pricingPolicy: 'hourly',

    staff: [],
    staffSeq: 0,
    upgrades: [],
    facilities: [],
  };
}
